import {
  addDays,
  addMinutes,
  format,
  isValid,
  parseISO,
  startOfDay,
  startOfHour,
} from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import type { SmartSchedulingSlotsPayload } from "../../api/smart-scheduling/smart-scheduling.types";

export type UtcTimeframe = {
  start_at_utc: string;
  end_at_utc: string;
};

export type LocalTimeframe = {
  start: Date; // instant, rendered in local timezone
  end: Date; // instant, rendered in local timezone
};

const QUARTER_HOUR_MINUTES = 15;
const QUARTER_HOUR_MS = QUARTER_HOUR_MINUTES * 60 * 1000;

/**
 * Parses API utc-like timestamps robustly.
 * Expected examples: "2026-01-07T10:00:00Z" or "2026-01-07 10:00:00"
 */
export function parseUtcTimestamp(value: string): Date {
  // First try ISO parsing
  const isoParsed = parseISO(value);
  if (isValid(isoParsed))
    return isoParsed;

  // Try to normalize "YYYY-MM-DD HH:mm:ss" into ISO with Z
  const normalized = value.includes("T") ? value : value.replace(" ", "T");
  const withZone = /z|[+-]\d{2}:?\d{2}$/i.test(normalized)
    ? normalized
    : `${normalized}Z`;

  const normalizedParsed = parseISO(withZone);
  if (isValid(normalizedParsed))
    return normalizedParsed;

  // Last resort: Date constructor
  return new Date(value);
}

export function toLocalTimeframes(
  timeframes: UtcTimeframe[],
): LocalTimeframe[] {
  return timeframes.map(tf => ({
    start: parseUtcTimestamp(tf.start_at_utc),
    end: parseUtcTimestamp(tf.end_at_utc),
  }));
}

/**
 * Expands timeframes into a map keyed by local day (yyyy-MM-dd) so that
 * day columns are derived from user timezone.
 */
export function groupTimeframesByLocalDay(localTimeframes: LocalTimeframe[]) {
  const map = new Map<string, LocalTimeframe[]>();

  for (const tf of localTimeframes) {
    const startDay = startOfDay(tf.start);
    const endDay = startOfDay(tf.end);

    for (
      let day = startDay;
      day.getTime() <= endDay.getTime();
      day = addDays(day, 1)
    ) {
      const key = format(day, "yyyy-MM-dd");
      const existing = map.get(key) ?? [];
      existing.push(tf);
      map.set(key, existing);
    }
  }

  return map;
}

export function getGridBounds(localTimeframes: LocalTimeframe[]) {
  if (localTimeframes.length === 0) {
    const now = new Date();
    return {
      start: startOfHour(now),
      end: addMinutes(startOfHour(now), 8 * 60),
    };
  }

  let minStart = localTimeframes[0]!.start;
  let maxEnd = localTimeframes[0]!.end;

  for (const tf of localTimeframes) {
    if (tf.start.getTime() < minStart.getTime())
      minStart = tf.start;
    if (tf.end.getTime() > maxEnd.getTime())
      maxEnd = tf.end;
  }

  const start = startOfHour(minStart);
  const endHour = startOfHour(maxEnd);
  const end
    = maxEnd.getTime() === endHour.getTime() ? endHour : addMinutes(endHour, 60);

  return { start, end };
}

export function buildQuarterHourTicks(start: Date, end: Date) {
  const ticks: Date[] = [];
  for (
    let t = start;
    t.getTime() < end.getTime();
    t = addMinutes(t, QUARTER_HOUR_MINUTES)
  ) {
    ticks.push(t);
  }
  return ticks;
}

export function isSlotAvailable(
  slotStart: Date,
  dayTimeframes: LocalTimeframe[],
) {
  const slotStartMs = slotStart.getTime();
  const slotEndMs = slotStartMs + QUARTER_HOUR_MS;

  return dayTimeframes.some((tf) => {
    const startMs = tf.start.getTime();
    const endMs = tf.end.getTime();
    return slotStartMs >= startMs && slotEndMs <= endMs;
  });
}

export function mergeQuarterHourSlotsToIntervals(slotStarts: Date[]) {
  const sorted = [...slotStarts].sort((a, b) => a.getTime() - b.getTime());
  const intervals: { start: Date; end: Date }[] = [];

  let currentStart: Date | null = null;
  let currentEnd: Date | null = null;

  for (const slotStart of sorted) {
    const slotEnd = addMinutes(slotStart, QUARTER_HOUR_MINUTES);

    if (!currentStart || !currentEnd) {
      currentStart = slotStart;
      currentEnd = slotEnd;
      continue;
    }

    if (slotStart.getTime() === currentEnd.getTime()) {
      currentEnd = slotEnd;
      continue;
    }

    intervals.push({ start: currentStart, end: currentEnd });
    currentStart = slotStart;
    currentEnd = slotEnd;
  }

  if (currentStart && currentEnd)
    intervals.push({ start: currentStart, end: currentEnd });

  return intervals;
}

export function toApiTimezoneIntervals(
  intervals: { start: Date; end: Date }[],
  apiTimezone?: string,
): SmartSchedulingSlotsPayload {
  if (!apiTimezone) {
    // Fallback: format in user's local timezone if API timezone is missing
    return {
      intervals: intervals.map(({ start, end }) => ({
        start: format(start, "yyyy-MM-dd HH:mm:ss"),
        end: format(end, "yyyy-MM-dd HH:mm:ss"),
      })),
    };
  }

  return {
    intervals: intervals.map(({ start, end }) => ({
      start: formatInTimeZone(start, apiTimezone, "yyyy-MM-dd HH:mm:ss"),
      end: formatInTimeZone(end, apiTimezone, "yyyy-MM-dd HH:mm:ss"),
    })),
  };
}

export function buildIntervalsPayloadFromSelectedSlotIsos(
  selectedSlotIsos: Iterable<string>,
  apiTimezone?: string,
): SmartSchedulingSlotsPayload {
  const slotStarts: Date[] = [];
  for (const iso of selectedSlotIsos) {
    const d = parseISO(iso);
    if (isValid(d))
      slotStarts.push(d);
  }

  const intervals = mergeQuarterHourSlotsToIntervals(slotStarts);
  return toApiTimezoneIntervals(intervals, apiTimezone);
}
